import { KatSpinner } from '@amzn/katal-react';
import React from 'react';
import LoadingIllustration from 'src/assets/su-loading-illustration.png';
import { LOADING_ILLUSTRATION_ALT } from 'src/constants/entity';

export interface LoadingViewProps extends React.ComponentProps<'div'> {
  loadingMessage?: string;
}

/**
 * A standardized loading view component that displays a loading illustration, optional message,
 * and Katal spinner. Designed for consistent loading states across the application.
 *
 * @example
 * // Basic usage with spinner only
 * <LoadingView />
 *
 * // With loading message
 * <LoadingView loadingMessage="Loading your content..." />
 *
 * // With custom styling
 * <LoadingView
 *   loadingMessage="Please wait"
 *   className="my-su-space-05"
 * />
 *
 * @component
 * @param {LoadingViewProps} props - Component props
 * @param {string} [props.loadingMessage] - Optional message to display above the spinner
 * @param {string} [props.className] - Additional CSS classes for the container
 * @returns {JSX.Element} A centered loading view with illustration, optional message, and spinner
 */

const LoadingView = ({ loadingMessage, className, ...rest }: LoadingViewProps) => {
  return (
    <div
      className={`m-auto flex w-full max-w-[30rem] flex-col items-center gap-su-space-05 ${className || ''}`}
      {...rest}
    >
      <img src={LoadingIllustration} className="w-full max-w-[22rem]" alt={LOADING_ILLUSTRATION_ALT} />
      {loadingMessage && <b className="whitespace-pre-line text-pretty text-center">{loadingMessage}</b>}
      <KatSpinner />
    </div>
  );
};

export default LoadingView;
